import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CfFlowResponse } from './api/cloudflareFlowApi';
import { cfEventSubjectOf } from './cfEventProperties';
import { useCfFlowEventTracker } from './useCfFlowEventTracker';

export const READY_EMAIL_EVENT = 'Automated CF Integration: Ready Email';

/** `Ready Email.type`; the HubSpot buttons link with `email=<type>`. */
const READY_EMAIL_TYPES = ['zone_ready', '48h_reminder'] as const;

type ReadyEmailType = (typeof READY_EMAIL_TYPES)[number];

export const isReadyEmailType = (value: string | null): value is ReadyEmailType =>
  (READY_EMAIL_TYPES as readonly (string | null)[]).includes(value);

const clickedKey = (flow: CfFlowResponse, type: ReadyEmailType) =>
  `cfReadyEmailClicked:${flow.domain}:${flow.createdAt}:${type}`;

const alreadyReported = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
};

const markReported = (key: string): void => {
  try {
    window.localStorage.setItem(key, String(Date.now()));
  } catch {
    // Storage unavailable: the click may be counted again, which beats not counting it.
  }
};

export const useReadyEmailClicked = (flow: CfFlowResponse | undefined): void => {
  const track = useCfFlowEventTracker(flow && cfEventSubjectOf(flow));
  const [searchParams] = useSearchParams();
  const type = searchParams.get('email');

  useEffect(() => {
    if (!flow || !isReadyEmailType(type)) return;

    const key = clickedKey(flow, type);
    if (alreadyReported(key)) return;

    markReported(key);
    track(READY_EMAIL_EVENT, { type, action: 'clicked' });
  }, [flow?.domain, flow?.createdAt, type]);
};
